/**
 * News data layer (Phase 1 #8).
 *
 * `GET /news/list` returns a page of articles plus the resolved country. The
 * list is page-based and consumed via `useInfiniteQuery`, keyed off `next`.
 * Article detail is fetched with `&slug=<slug>` which removes that slug from the
 * "more news" list — the detail page reads it back out of the cached list page
 * via `useNewsArticle`, which finds it by slug across pages.
 */
import {
  useInfiniteQuery,
  useQuery,
  type InfiniteData,
  type QueryKey,
} from '@tanstack/react-query';

import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import type { NewsArticle, NewsListResult } from '@/types/api';

/** Default page size for news lists. */
const PAGE_LIMIT = 12;

/** Default country when none is supplied (matches the API default). */
export const DEFAULT_COUNTRY = 'in';

type NewsListInfinite = InfiniteData<NewsListResult, number>;

function toQuery(params: Record<string, unknown> = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Infinite news list (`GET /news/list`). Pass a country code (defaults to the
 * API's resolved country / `in`). When `slug` is set the API drops that article
 * from the list — used for the "More news" rail on the detail page.
 */
export function useNewsList(country?: string, slug?: string) {
  const query = { country, slug, limit: PAGE_LIMIT };
  return useInfiniteQuery<
    NewsListResult,
    Error,
    NewsListInfinite,
    QueryKey,
    number
  >({
    queryKey: queryKeys.news.list(query),
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      api.get<NewsListResult>(`news/list${toQuery({ ...query, page: pageParam })}`),
    getNextPageParam: (lastPage, _all, lastParam) =>
      lastPage.next ? lastParam + 1 : undefined,
  });
}

/**
 * A single news article. The API surfaces articles only through `news/list`, so
 * the detail view fetches the first page for the country and finds the matching
 * `slug`. The list strips the country prefix on the bare `slug`, so match on the
 * suffix.
 */
export function useNewsArticle(country: string, slug: string | undefined) {
  return useQuery({
    queryKey: queryKeys.news.detail(country, slug ?? ''),
    enabled: !!slug,
    // Per-article one-shot — browsing the news section can fetch
    // many articles; let unmounted ones drop after 1 min.
    gcTime: 60_000,
    queryFn: async () => {
      const target = (slug ?? '').toLowerCase();
      // The API only exposes articles through `news/list`, so walk subsequent
      // pages until the matching slug shows up. Cap at 5 pages to avoid a
      // runaway crawl when the slug is stale or missing.
      const MAX_PAGES = 5;
      const PAGE_SIZE = 50;
      let lastPage: NewsListResult | null = null;
      let page = 1;
      let hasMore = true;
      let found: NewsArticle | undefined;
      while (page <= MAX_PAGES && hasMore && !found) {
        const result = await api.get<NewsListResult>(
          `news/list${toQuery({ country, limit: PAGE_SIZE, page })}`,
        );
        lastPage = result;
        found = (result.list ?? []).find((a) => {
          const s = (a.slug ?? '').toLowerCase();
          return s === target || s.endsWith(`/${target}`);
        });
        hasMore = Boolean(result.next);
        page += 1;
      }
      return {
        article: found ?? null,
        country: lastPage?.country,
        country_code: lastPage?.country_code,
      };
    },
  });
}

export type { NewsArticle, NewsListResult };
